/**
 * Google sign-in (ADR-0004): Google Identity Services gives an ID token, and Supabase verifies it with
 * signInWithIdToken. GIS gets the SHA-256 of a one-time nonce, and Supabase gets the raw nonce, so a token
 * captured elsewhere can't be replayed. Off until VITE_GOOGLE_CLIENT_ID is set (the owner's OAuth client);
 * the email code is always there as the fallback. Still to check on a real iPhone in standalone mode (spike S-1).
 */
interface GisId {
  initialize(o: { client_id: string; callback: (r: { credential?: string }) => void; nonce: string; use_fedcm_for_prompt?: boolean; ux_mode?: 'popup' | 'redirect'; auto_select?: boolean }): void;
  renderButton(el: HTMLElement, o: { type?: string; theme?: string; size?: string; text?: string; shape?: string; width?: number; logo_alignment?: string }): void;
}
declare global {
  interface Window {
    google?: { accounts: { id: GisId } };
  }
}

export const googleClientId = (): string | undefined => (import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined) || undefined;

const hex = (b: ArrayBuffer) => [...new Uint8Array(b)].map((x) => x.toString(16).padStart(2, '0')).join('');

export async function makeNonce(): Promise<{ raw: string; hashed: string }> {
  const raw = hex(crypto.getRandomValues(new Uint8Array(32)).buffer);
  const hashed = hex(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(raw)));
  return { raw, hashed };
}

let loading: Promise<GisId> | null = null;
/** Load the GIS script once, only on the sign-in screen (never on the workout path). */
export function loadGis(doc: Document = document): Promise<GisId> {
  if (typeof window !== 'undefined' && window.google?.accounts?.id) return Promise.resolve(window.google.accounts.id);
  loading ??= new Promise<GisId>((ok, fail) => {
    const s = doc.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.onload = () => (window.google?.accounts?.id ? ok(window.google.accounts.id) : fail(new Error('Google sign-in did not load')));
    s.onerror = () => ((loading = null), fail(new Error('Google sign-in did not load')));
    doc.head.appendChild(s);
  });
  return loading;
}
